/**
 * ANH DA NHUNG CUA MOT TRANG - nap, chieu lenh len, va giu trang thai keo.
 *
 * Cua so nhin vao mot kha nang cua loi, dung mau `asTextEditCore` trong
 * ui/textEditCore.ts: hop dong PdfCore khong mo ta "liet ke anh cua trang", va
 * dieu do la dung - do la mot tien nghi cua duong doc, khong phai mot phep toan
 * tren tai lieu. Nen ta kiem tra cau truc luc chay va TU LUI ve "khong co anh"
 * khi loi dang chay khong biet lam viec do, thay vi ep kieu bua roi vo.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DocumentId, EditCommand, PdfCore, PdfRect, Result } from '@pdfeditor/types';
import type { PageImagesInfo } from '../core/PdfCoreBrowser';
import { projectImages, type ProjectedImage } from '../core/content/projection';
import { vectorClickExplanation } from '../core/content/embeddedImages';

export interface ImageContentCapableCore {
  pageImages(doc: DocumentId, pageIndex: number): Promise<Result<PageImagesInfo>>;
}

export function asImageContentCore(core: unknown): ImageContentCapableCore | null {
  const candidate = core as Partial<ImageContentCapableCore> | null;
  if (!candidate || typeof candidate.pageImages !== 'function') return null;
  return candidate as ImageContentCapableCore;
}

export interface PageImagesState {
  /** Anh dang co hieu luc, DA ap danh sach lenh. Anh da xoa khong nam trong day. */
  images: ProjectedImage[];
  /**
   * Ly do KHONG sua duoc anh tren trang nay, hoac null.
   *
   * Nguoi dung van chon duoc anh khi co ly do - o chon van hien - nhung khong
   * co tay nam. Mot tay nam keo khong duoc la mot tay nam hong.
   */
  refusal: string | null;
  /** Loi giai thich khi bam trung do hoa vecto. null khi trang khong co vecto. */
  vectorNote: string | null;
  loading: boolean;
}

const EMPTY: PageImagesState = { images: [], refusal: null, vectorNote: null, loading: false };

/**
 * Nap va chieu anh cua mot trang.
 *
 * `enabled` sai thi KHONG goi loi: getOperatorList() phan tich lai ca trang, va
 * goi no cho moi trang trong vung dem chi de phong khi nguoi dung doi sang cong
 * cu Chon la mot cai gia rat that tren tai lieu day anh.
 */
export function useEmbeddedImages(
  core: PdfCore | null,
  doc: DocumentId | null,
  pageIndex: number | null,
  commands: readonly EditCommand[],
  enabled: boolean,
): PageImagesState {
  const [info, setInfo] = useState<PageImagesInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !core || !doc || pageIndex === null) {
      setInfo(null);
      return;
    }
    const capable = asImageContentCore(core);
    if (!capable) {
      setInfo(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void capable
      .pageImages(doc, pageIndex)
      .then((result) => {
        if (cancelled) return;
        setInfo(result.ok ? result.value : null);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setInfo(null);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [core, doc, pageIndex, enabled]);

  return useMemo((): PageImagesState => {
    if (!info) return { ...EMPTY, loading };
    const projected = projectImages(info.baselines, commands).filter((i) => !i.removed);
    return {
      images: projected,
      refusal: info.refusal,
      vectorNote: vectorClickExplanation(info.scan),
      loading,
    };
  }, [info, commands, loading]);
}

/**
 * Vi tri TAM cua anh trong luc dang keo.
 *
 * Song rieng khoi danh sach lenh co chu dich: mot cu keo chuot sinh ra hang
 * chuc khung hinh, va ghi nhan mot lenh o moi khung se lam ngap lich su. Lenh
 * chi duoc phat KHI NHA TAY. Trong luc keo, cai duy nhat doi la ban do nay.
 */
export function useImageDragOverlay() {
  const [overlay, setOverlay] = useState<ReadonlyMap<string, PdfRect>>(new Map());

  const setRect = useCallback((id: string, rect: PdfRect) => {
    setOverlay((prev) => {
      const next = new Map(prev);
      next.set(id, rect);
      return next;
    });
  }, []);

  const clear = useCallback((id: string) => {
    setOverlay((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  return { overlay, setRect, clear };
}
